import { Offcanvas, Form, Card } from "react-bootstrap";
import CustomButton from "../../components/CustomButton/CustomButton";
import { useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import WebService from "../../utility/WebService";
import { toast } from "react-toastify";
import Multiselect from "multiselect-react-dropdown";

interface PlanFormProps {
  show: boolean;
  handleClose: () => void;
  data?: any;
}

type FeatureAccessItem = {
  featureId: string;
  allowed: boolean;
  value?: string;
};

type FormData = {
  planName: string;
  turnover: string;
  companyRevenueRange: string[];
  amount: number;
  duration: number;
  industryDocumentDownloadMaxLimit: number;
  afterLimitExceedDiscountedPer: number;
  frequency: "MONTH" | "YEAR";
  bgColor: string;
  numberOfMembers: number;

  featureAccess: FeatureAccessItem[];
};

const AddEditPlan = ({ show, handleClose, data }: PlanFormProps) => {
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors }
  } = useForm<FormData>({
    defaultValues: { companyRevenueRange: [], featureAccess: [] }
  });

  const [isLoading, setLoading] = useState(false);
  const [turnoverList, setTurnoverList] = useState<any[]>([]);
  const [featuresBySection, setFeaturesBySection] = useState<any[]>([]);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  // Fetch turnover list
  useEffect(() => {
    WebService.getAPI({ action: "turnover-range/list" })
      .then((res: any) => setTurnoverList(res.data || []))
      .catch(() => setTurnoverList([]));
  }, []);

  // Fetch sectional features
  useEffect(() => {
    WebService.getAPI({ action: "memberShipBenifits/section-wise-list" })
      .then((res: any) => setFeaturesBySection(res.data || []))
      .catch(() => setFeaturesBySection([]));
  }, []);

  // Prefill on open
  useEffect(() => {
    if (show) {
      reset({
        planName: data?.planName || "",
        turnover: data?.turnover || "",
        companyRevenueRange: data?.companyRevenueRange?.map((x: any) => x._id) || [],
        amount: data?.amount || 0,
        duration: data?.duration || 1,
        industryDocumentDownloadMaxLimit:
          data?.industryDocumentDownloadMaxLimit ?? undefined,
        afterLimitExceedDiscountedPer:
          data?.afterLimitExceedDiscountedPer ?? undefined,
        frequency: data?.frequency || "YEAR",
        bgColor: data?.bgColor || "#FFFFFF",
        numberOfMembers: data?.numberOfMembers ?? 1,

        featureAccess:
          data?.featureAccess
            ?.filter((fa: any) => fa?.featureId?._id).map((fa: any) => ({
              featureId: fa.featureId._id,
              allowed: fa.allowed,
              value: fa.value || ""
            })) || []
      });
    } else {
      reset({});
    }
  }, [show, data, reset]);

  // Update text value
  const updateTextValue = (featureId: string, text: string) => {
    const list = watch("featureAccess") || [];
    const found = list.find((f) => f?.featureId && f.featureId === featureId);

    let updated;

    // Cannot update value if toggle is OFF
    if (found && !found.allowed) return;

    if (found) {
      updated = list.map((f) =>
        f.featureId === featureId ? { ...f, value: text } : f
      );
    } else {
      updated = [...list, { featureId, allowed: true, value: text }];
    }

    setValue("featureAccess", updated);
  };

  // Toggle allow
  const toggleFeature = (featureId: string) => {
    const list = watch("featureAccess") || [];
    const found = list.find((f) => f.featureId === featureId);

    let updated;

    if (found) {
      updated = list.map((f) =>
        f.featureId === featureId
          ? {
            ...f,
            allowed: !f.allowed,
            value: !f.allowed ? f.value : "" // OFF → reset value
          }
          : f
      );
    } else {
      updated = [...list, { featureId, allowed: true, value: "" }];
    }

    setValue("featureAccess", updated);
  };

  const onSubmit = (request: FormData) => {
    if (!request.companyRevenueRange.length) {
      toast.error("Please select at least one turnover range");
      return;
    }

    if (!request.turnover) {
      toast.error("Turnover is required");
      return;
    }
    setLoading(true);
    const body = { ...request };

    const apiCall = data?._id
      ? WebService.putAPI({
        action: `plan/edit-plan/${data._id}`,
        body
      })
      : WebService.postAPI({
        action: "plan/add-plan",
        body
      });

    apiCall
      .then((res: any) => {
        toast.success(res.message);
        reset();
        handleClose();
      })
      .catch((e) => toast.error(e.response?.data?.message || "Error"))
      .finally(() => setLoading(false));
  };

  return (
    <Offcanvas
      show={show}
      onHide={() => {
        reset();
        handleClose();
      }}
      placement="end"
      className="offcanvas-custom"
    >
      <Offcanvas.Header closeButton>
        <Offcanvas.Title>{data ? "Edit Plan" : "Add Plan"}</Offcanvas.Title>
      </Offcanvas.Header>

      <Offcanvas.Body>
        <Form>
          {/* ------------------ */}
          {/* PLAN DETAILS */}
          {/* ------------------ */}

          <Card className="p-3 mt-3">
            <h6 className="fw-bold mb-3">Plan Details</h6>

            <Form.Group className="mb-2">
              <Form.Label>Plan Name <span className="text-danger">*</span></Form.Label>
              <Form.Control
                {...register("planName", { required: "Plan name is required" })}
              />
              {errors.planName && (
                <div className="error-message">{errors.planName.message}</div>
              )}
            </Form.Group>

            {/* ⭐ NEW Turnover Field */}
            <Form.Group className="mb-2">
              <Form.Label>Turnover <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter turnover (e.g. USD 10M - 100M)"
                {...register("turnover", {
                  required: "Turnover is required",
                  maxLength: {
                    value: 100,
                    message: "Turnover is too long"
                  }
                })}
              />
              {errors.turnover && (
                <div className="error-message">{errors.turnover.message}</div>
              )}
            </Form.Group>

            <Form.Group className="mb-2">
              <Form.Label>Turnover Range <span className="text-danger">*</span></Form.Label>
              <Multiselect
                options={turnoverList}
                displayValue="name"
                selectedValues={turnoverList.filter((item: any) =>
                  (watch("companyRevenueRange") || []).includes(item._id)
                )}
                onSelect={(selected: any) =>
                  setValue(
                    "companyRevenueRange",
                    selected.map((x: any) => x._id)
                  )
                }
                onRemove={(selected: any) =>
                  setValue(
                    "companyRevenueRange",
                    selected.map((x: any) => x._id)
                  )
                }
                showCheckbox
              />
            </Form.Group>

            <Form.Group className="mb-2">
              <Form.Label>Amount <span className="text-danger">*</span></Form.Label>
              <Form.Control type="number" {...register("amount", { required: "Amount is required" })} />
              {errors.amount && (
                <div className="error-message">{errors.amount.message}</div>
              )}
            </Form.Group>

            {/* <Form.Group className="mb-2">
              <Form.Label>Duration (Year) <span className="text-danger">*</span></Form.Label>
              <Form.Control type="number" {...register("duration")} />
            </Form.Group> */}

            <Form.Group className="mb-2">
              <Form.Label>Document Download Limit <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="number"
                placeholder="-1 for Unlimited"
                {...register("industryDocumentDownloadMaxLimit", { required: "Download Limit is required" })}
              />
              {errors.industryDocumentDownloadMaxLimit && (
                <div className="error-message">{errors.industryDocumentDownloadMaxLimit.message}</div>
              )}
            </Form.Group>

            <Form.Group className="mb-2">
              <Form.Label>After Limit Discount (%) <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="number"
                placeholder="e.g. 20"
                {...register("afterLimitExceedDiscountedPer", { required: "Discount Percentage is required" })}
              />
              {errors.afterLimitExceedDiscountedPer && (
                <div className="error-message">{errors.afterLimitExceedDiscountedPer.message}</div>
              )}
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>
                Number of Members <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="number"
                placeholder="Enter number of members"
                {...register("numberOfMembers", {
                  required: "Number of Members is required",
                  min: {
                    value: 1,
                    message: "At least 1 member required"
                  }
                })}
              />
              {errors.numberOfMembers && (
                <div className="error-message">
                  {errors.numberOfMembers.message}
                </div>
              )}
            </Form.Group>

            {/* <Form.Group className="mb-2">
              <Form.Label>Frequency <span className="text-danger">*</span></Form.Label>
              <Form.Select {...register("frequency")}>
                <option value="YEAR">Yearly</option>
                <option value="MONTH">Monthly</option>
              </Form.Select>
            </Form.Group> */}

            <Form.Group className="mb-2">
              <Form.Label>Background Color</Form.Label>
              <Form.Control type="color" {...register("bgColor")} />
            </Form.Group>
          </Card>

          {/* ------------------ */}
          {/* FEATURE ACCESS */}
          {/* ------------------ */}

          <Card className="p-3 mt-3">
            <h6 className="fw-bold mb-3">Feature Access (Membership Benefits Comparison)</h6>

            {featuresBySection.map((section: any) => {
              const secId = section.sectionId._id;

              return (
                <div key={secId} className="mb-3 border rounded">
                  <div
                    className="p-3 bg-light d-flex justify-content-between"
                    onClick={() =>
                      setExpanded((prev) => ({
                        ...prev,
                        [secId]: !prev[secId]
                      }))
                    }
                    style={{ cursor: "pointer" }}
                  >
                    <span className="fw-bold">{section.sectionId.title}</span>
                    <span>{expanded[secId] ? "▲" : "▼"}</span>
                  </div>

                  {expanded[secId] && (
                    <div className="p-2">
                      {section.features.map((feature: any) => {
                        const existing =
                          watch("featureAccess")?.find(
                            (f) => f.featureId === feature._id
                          ) ||
                          ({
                            featureId: feature._id,
                            allowed: false,
                            value: ""
                          } as FeatureAccessItem);

                        return (
                          <div key={feature._id} className="border-bottom p-2">
                            <div className="d-flex justify-content-between align-items-center">
                              <span>{feature.title}</span>

                              <div className="d-flex flex-column align-items-end">
                                <Form.Check
                                  type="switch"
                                  checked={existing.allowed}
                                  onChange={() => toggleFeature(feature._id)}
                                />

                                {/* SMALL TEXT FIELD (Below Toggle) */}
                                <input
                                  type="text"
                                  placeholder="Enter value"
                                  value={existing.value || ""}
                                  disabled={!existing.allowed}
                                  onChange={(e) =>
                                    updateTextValue(
                                      feature._id,
                                      e.target.value
                                    )
                                  }
                                  style={{
                                    width: "130px",
                                    height: "24px",
                                    marginTop: "4px",
                                    fontSize: "12px",
                                    padding: "3px 5px",
                                    opacity: existing.allowed ? 1 : 0.4
                                  }}
                                />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </Card>
        </Form>
      </Offcanvas.Body>

      <div className="p-3">
        <CustomButton
          isLoading={isLoading}
          label="Save"
          className="w-100"
          onClick={handleSubmit(onSubmit)}
        />
      </div>
    </Offcanvas>
  );
};

export default AddEditPlan;
